import React from "react";
import { useNode, useEditor } from "@craftjs/core";
import { useInputLogic } from './hooks/useInputLogic';
import { useInputStyles } from './hooks/useInputStyles';
import { buildInputClasses, buildControlClasses } from './utils/classUtils';
import { DEFAULT_PROPS } from './constants/inputConstants';
import { InputSettings } from './InputSettings';

export const Input = ({
  name,
  qwantumId,
  value,
  inputName,
  placeholder = "Enter text",
  type = "text",
  readonly = false,
  disabled = false,
  allowEditing = true,
  bulmaSize = "",
  bulmaColor = "",
  bulmaStyle = "",
  bulmaState = "",
  hasLeftIcon = false,
  hasRightIcon = false,
  leftIconClass = "fas fa-user",
  rightIconClass = "fas fa-check",
  labelText = "",
  showSemicolon = true,
  fontFamily = "inherit",
  cssClass,
  hidden,
  onSave,
  style,
  ...props
}) => {
  const {
    connectors: { connect, drag },
    isSelected,
    isHovered,
    actions: { setProp }
  } = useNode((state) => ({
    isSelected: state.events.selected,
    isHovered: state.events.hovered
  }));

  const { enabled } = useEditor((state) => ({
    enabled: state.options.enabled
  }));

  const {
    inputRef,
    getInitialValue,
    handleBlur,
    handleDateBlur,
    handleDateFocus
  } = useInputLogic({ type, value, qwantumId, onSave, enabled, allowEditing, setProp });

  const { containerStyle, labelStyle, inputStyle } = useInputStyles(
    props, 
    enabled, 
    isSelected, 
    isHovered
  );

  if (hidden) return null;

  const renderInput = () => {
    const commonProps = {
      ref: inputRef,
      name: inputName,
      placeholder: type === 'date' ? (placeholder || "DD/MM/YYYY") : placeholder,
      readOnly: readonly,
      disabled: disabled,
      className: buildInputClasses({ bulmaSize, bulmaColor, bulmaStyle, bulmaState }),
      style: inputStyle
    };

    if (type === 'date') {
      return (
        <input
          {...commonProps}
          type="text"
          defaultValue={getInitialValue()}
          onBlur={handleDateBlur}
          onFocus={handleDateFocus}
        />
      );
    }

    return (
      <input
        {...commonProps}
        type={type}
        defaultValue={value || ""}
        onBlur={handleBlur}
      />
    );
  };

  return (
    <div
      ref={ref => enabled ? connect(drag(ref)) : connect(ref)}
      name={name}
      className={cssClass}
      style={containerStyle}
      {...props}
    >
      {labelText && (
        <label style={labelStyle}>
          {labelText}{showSemicolon ? ':' : ''}
        </label>
      )}

      <div className={buildControlClasses({ hasLeftIcon, hasRightIcon, inputClass: props.inputClass })}>
        {renderInput()}
        {hasLeftIcon && (
          <span className="icon is-small is-left">
            <i className={leftIconClass}></i>
          </span>
        )}
        {hasRightIcon && (
          <span className="icon is-small is-right">
            <i className={rightIconClass}></i>
          </span>
        )}
      </div>
    </div>
  );
};

Input.craft = {
  props: DEFAULT_PROPS,
  related: {
    settings: InputSettings
  }
};